import { AppRouteRecordRaw } from '../types';
import { PARTNER_FEATURE_FLAGS } from '@/constants/partner-features';

const Body = () => import('@/components/body.vue');
const AiAtRydOverview = () => import('@/pages/ai-at-ryd/overview/AiAtRydOverview.vue');
const AiAtRydConnect = () => import('@/pages/ai-at-ryd/connect/AiAtRydConnect.vue');
const AiAtRydCapabilities = () => import('@/pages/ai-at-ryd/capabilities/AiAtRydCapabilities.vue');
const AiAtRydInterfaceDemo = () =>
  import('@/pages/ai-at-ryd/interface-demo/AiAtRydInterfaceDemo.vue');
const AiAtRydSecurity = () => import('@/pages/ai-at-ryd/security/AiAtRydSecurity.vue');
const AiAtRydActivity = () => import('@/pages/ai-at-ryd/activity/AiAtRydActivity.vue');
const AiAtRydDevelopers = () => import('@/pages/ai-at-ryd/developers/AiAtRydDevelopers.vue');
const UsageAndLimits = () =>
  import('@/pages/communityandsupport/usage-and-limits/UsageAndLimits.vue');
const AiAtRydDownload = () => import('@/pages/ai-at-ryd/download/AiAtRydDownload.vue');
const AiAtRydConnectAuthorize = () =>
  import('@/pages/ai-at-ryd/connect-authorize/AiAtRydConnectAuthorize.vue');

/**
 * "AI at ryd" — partner documentation section, switched on per partner with the `aiAtRydEnabled`
 * feature flag (Partner Administration → Partner Features). The flag is on the parent route so
 * every child is gated by the global router guard. ryd-internal users always pass the gate.
 */
const meta = (title: string, breadcrumb: string) => ({
  title: `${title} | ryd Portal`,
  breadcrumb,
  requiresAuth: true,
});

export const aiAtRydRoutes: AppRouteRecordRaw[] = [
  {
    path: '/ai-at-ryd',
    component: Body,
    meta: {
      breadcrumb: 'AI at ryd',
      requiresPartnerFeature: PARTNER_FEATURE_FLAGS.AI_AT_RYD,
      partnerFeatureOpenForInternalUsers: true,
    },
    children: [
      { path: '', redirect: '/ai-at-ryd/overview' },
      {
        path: 'overview',
        name: 'AiAtRydOverview',
        component: AiAtRydOverview,
        meta: meta('Overview', 'Overview'),
      },
      {
        path: 'capabilities',
        name: 'AiAtRydCapabilities',
        component: AiAtRydCapabilities,
        meta: meta('Tools and Examples', 'Tools and Examples'),
      },
      {
        path: 'connect',
        name: 'AiAtRydConnect',
        component: AiAtRydConnect,
        meta: meta('Connect an Assistant', 'Connect an Assistant'),
      },
      {
        path: 'security',
        name: 'AiAtRydSecurity',
        component: AiAtRydSecurity,
        meta: meta('Security and Privacy', 'Security and Privacy'),
      },
      {
        path: 'interface-demo',
        name: 'AiAtRydInterfaceDemo',
        component: AiAtRydInterfaceDemo,
        meta: meta('Interface Examples', 'Interface Examples'),
      },
      {
        path: 'activity',
        name: 'AiAtRydActivity',
        component: AiAtRydActivity,
        meta: meta('AI Activity Log', 'Activity Log'),
      },
      {
        path: 'developers',
        name: 'AiAtRydDevelopers',
        component: AiAtRydDevelopers,
        meta: meta('Developer Guide', 'Developer Guide'),
      },
    ],
  },
  /**
   * Usage & Limits (ticket 2026-10-18-usage-limits-and-traffic): in the AI at ryd menu group but
   * for EVERY signed-in user, so it is its own route record without the parent's feature gate.
   * Partner page: always the viewer's own company. The former path under /support-and-feedback
   * redirects here.
   */
  /**
   * File download for AI assistants that cannot return a file (ryd-portal-mcp links
   * `/ai-at-ryd/download?file=<kind>/<id>`): sign-in only, no feature gate, not in the menu or
   * search registry. The file is fetched with the user's own session and the regular page
   * permissions, so the link carries no secret.
   */
  {
    path: '/ai-at-ryd/download',
    component: Body,
    meta: { breadcrumb: 'AI at ryd' },
    children: [
      {
        path: '',
        name: 'aiFileDownload',
        component: AiAtRydDownload,
        meta: meta('Download a File', 'Download a File'),
      },
    ],
  },
  {
    path: '/ai-at-ryd/usage-and-limits',
    component: Body,
    // Same gate as the other AI at ryd pages (2026-10-19): a partner without the flag must not
    // reach it by URL either.
    meta: {
      breadcrumb: 'AI at ryd',
      requiresPartnerFeature: PARTNER_FEATURE_FLAGS.AI_AT_RYD,
      partnerFeatureOpenForInternalUsers: true,
    },
    children: [
      {
        path: '',
        name: 'usageAndLimits',
        component: UsageAndLimits,
        meta: { ...meta('Usage & Limits', 'Usage & Limits'), headTitle: 'Usage & Limits' },
      },
    ],
  },
  /**
   * Consent page of the ryd AI connector: ryd-portal-mcp's /oauth/authorize redirects here with
   * `request_id`. Only sign-in is required (the global guard brings the user back after login and
   * 2FA); eligibility (company flag, ryd accounts refused) is decided by rp-users and the MCP
   * server, so no partner-feature gate. Not in the menu or search registry.
   */
  {
    path: '/connect',
    component: Body,
    children: [
      {
        path: 'authorize',
        name: 'mcpConnectAuthorize',
        component: AiAtRydConnectAuthorize,
        meta: meta('Connect AI Assistant', 'Connect AI Assistant'),
      },
    ],
  },
];
